import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { compassColors } from "@utils/styling";
import Button from "src/design-system/components/Button/Button";
import { Text } from "@components/core/Text";
import {
  fontLargeHeadline,
  fontP3Regular,
} from "src/design-system/styles/fonts";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-height: 80vh;
  background-color: ${compassColors.white};
  border-radius: 20px 20px 0 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DragHandle = styled.div`
  width: 48px;
  height: 4px;
  background-color: ${compassColors.grey1};
  border-radius: 2px;
  margin: 4px auto 0;
`;

const ButtonSection = styled.div`
  padding: 32px 16px 32px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  margin-top: auto;
`;

const StyledTitle = styled.h2`
  ${fontLargeHeadline};
  letter-spacing: -0.001em;
  line-height: 34px;
  font-weight: 400;
  text-align: left;
  margin: 32px 0 0;
  padding: 0 24px;
`;

const StyledSubtitle = styled.h2`
  ${fontP3Regular};
  color: ${compassColors.grey4};
  font-weight: 400;
  text-align: left;
  padding: 0 24px;
`;

interface ConfirmationModalProps {
  onClose: () => void;
  onPrimaryAction: () => void;
  dataCy?: string;
}

function ConfirmationModal({
  onClose,
  onPrimaryAction,
  dataCy,
}: ConfirmationModalProps) {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick} data-cy={dataCy}>
      <ModalContainer data-cy={`${dataCy}-container`}>
        <DragHandle data-cy={`${dataCy}-drag-handle`} />
        <StyledTitle data-cy={`${dataCy}-title`}>
          <Text i18nKey="globalExitButton:confirmationModal:title" />
        </StyledTitle>
        <StyledSubtitle data-cy={`${dataCy}-title`}>
          <Text i18nKey="globalExitButton:confirmationModal:subtitle" />
        </StyledSubtitle>
        <ButtonSection>
          <Button
            designStyle="medPrimaryCta"
            onClick={onPrimaryAction}
            data-cy={`${dataCy}-primary-button`}
            css={css`
              &:focus {
                outline: 0px;
                outline-offset: 0px;
              }
            `}
          >
            <Text i18nKey="globalExitButton:confirmationModal:primaryButtonText" />
          </Button>
          <Button
            designStyle="secondaryCta"
            onClick={onClose}
            data-cy={`${dataCy}-secondary-button`}
            css={css`
              color: ${compassColors.black};
              border: solid 1px ${compassColors.black};
              border-radius: 999px;
              &:hover:enabled {
                color: ${compassColors.black};
                background-color: ${compassColors.white};
              }
              &:focus {
                border: solid 1px ${compassColors.black};
                outline: 0px;
                outline-offset: 0px;
              }
            `}
          >
            <Text i18nKey="globalExitButton:confirmationModal:secondaryButtonText" />
          </Button>
        </ButtonSection>
      </ModalContainer>
    </ModalOverlay>
  );
}

export default ConfirmationModal;
